/**
 * Централизованный обработчик ошибок API
 * Обеспечивает единообразную обработку ошибок во всем приложении
 */

import { Store } from 'vuex';
import router from '../router';

export interface ApiError {
    status: number;
    data?: any;
    message?: string;
}

export interface ErrorHandlerOptions {
    /** Не показывать ошибку пользователю */
    silent?: boolean;
    /** Не редиректить на страницу forbidden при 403 */
    skipForbiddenRedirect?: boolean;
    /** Кастомная обработка ошибки */
    customHandler?: (error: ApiError) => boolean;
}

/**
 * Обрабатывает ошибки API запросов
 * @param err - Объект ошибки
 * @param store - Vuex store
 * @param options - Опции обработки
 * @returns true если ошибка обработана, false если нужно дополнительное действие
 */
export function handleApiError(
    err: any,
    store: Store<any>,
    options: ErrorHandlerOptions = {}
): boolean {
    // Если ошибки нет - логируем и выходим
    if (!err) {
        console.warn('[ErrorHandler] Empty error object received');
        return false;
    }

    // Нормализуем структуру ошибки
    const error: ApiError = {
        status: err.status ?? err.response?.status ?? 0,
        data: err.data ?? err.response?.data,
        message: err.message ?? err.response?.statusText
    };

    // Вызываем кастомный обработчик если есть
    if (options.customHandler && options.customHandler(error)) {
        return true;
    }

    // 401 - Unauthorized (не авторизован)
    if (error.status === 401) {
        // Токен истек или невалиден - просто возвращаемся
        // Axios interceptor уже обработает обновление токена
        return true;
    }

    // 403 - Forbidden (нет прав доступа)
    if (error.status === 403) {
        if (!options.skipForbiddenRedirect) {
            router.push('/forbidden');
        }
        return true;
    }

    // 500 - Internal Server Error
    if (error.status === 500) {
        if (!options.silent) {
            store.commit('SET_ISSHOWINTERNALSERVERERROR', true);
        }
        return true;
    }

    // 400 - Bad Request (ошибка валидации)
    if (error.status === 400 && error.data) {
        if (!options.silent) {
            store.commit('SET_ADD_ERROR', error.data);
        }
        return true;
    }

    // Остальные ошибки
    if (error.status > 0 && !options.silent) {
        store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
    }

    // Логируем ошибку в консоль для отладки
    if (process.env.NODE_ENV === 'development') {
        console.error('[API Error]', {
            status: error.status,
            data: error.data,
            message: error.message,
            original: err
        });
    }

    return true;
}
